import React, { useMemo, useState } from 'react';
import { Alert, Box, Button, Chip, LinearProgress, Paper, Stack, Typography } from '@mui/material';
import { CloudUpload, DeleteOutline, MovieOutlined } from '@mui/icons-material';
import { deleteCaseMedia, uploadCaseMedia } from '../../services/caseMedia';
import CaseMediaPlayer from '../CaseMediaPlayer';
import { caseManagerInsetSurfaceSx, caseManagerPalette, publicBodyFontFamily, publicSecondaryButtonSx } from './caseManagerStyles';

const CaseVideoUploadField = ({ label, description, purpose, value, caseId, uploadSessionId, onChange }) => {
  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');
  const busy = Boolean(status);
  const inputId = useMemo(() => `case-video-${purpose}`, [purpose]);

  const handleUpload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (!['video/mp4', 'video/quicktime', 'video/webm'].includes(file.type)) {
      setError('Choose an MP4, MOV, or WebM video.');
      return;
    }
    if (file.size > 500 * 1024 * 1024) {
      setError('Videos must be 500 MB or smaller.');
      return;
    }
    setError('');
    setProgress(0);
    setStatus('Uploading');
    try {
      const media = await uploadCaseMedia({
        file,
        purpose,
        caseId,
        uploadSessionId,
        onProgress: setProgress,
        onStatus: (next) => setStatus(next.status === 'processing' || next.status === 'uploaded' ? 'Optimising' : next.status),
      });
      onChange(media);
      setStatus('');
      setProgress(100);
    } catch (uploadError) {
      setStatus('');
      setError(uploadError.response?.data?.message || uploadError.message || 'Video upload failed.');
    }
  };

  const handleRemove = async () => {
    const previous = value;
    onChange(null);
    try {
      await deleteCaseMedia(previous);
    } catch {
      // Already-attached media is retained until a later lifecycle cleanup.
    }
  };

  return (
    <Paper sx={{ ...caseManagerInsetSurfaceSx, p: { xs: 1.5, md: 1.7 }, height: '100%' }}>
      <Stack spacing={1.3}>
        <Stack direction="row" spacing={1.2} alignItems="flex-start" justifyContent="space-between">
          <Box>
            <Typography sx={{ color: caseManagerPalette.ink, fontWeight: 800, fontFamily: publicBodyFontFamily }}>{label}</Typography>
            <Typography sx={{ mt: 0.35, color: caseManagerPalette.inkSoft, fontSize: '0.88rem', lineHeight: 1.45, fontFamily: publicBodyFontFamily }}>{description}</Typography>
          </Box>
          <MovieOutlined sx={{ color: caseManagerPalette.heroBlueMid }} />
        </Stack>

        {value ? (
          <>
            <CaseMediaPlayer media={value} compact />
            <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
              <Chip size="small" label={value.displayName || value.originalName || 'Video ready'} color="success" />
              <Button size="small" color="error" startIcon={<DeleteOutline />} onClick={handleRemove}>Remove</Button>
            </Stack>
          </>
        ) : (
          <Box>
            <input id={inputId} type="file" accept="video/mp4,video/quicktime,video/webm" hidden disabled={busy} onChange={handleUpload} />
            <label htmlFor={inputId}>
              <Button component="span" disabled={busy} startIcon={<CloudUpload />} sx={publicSecondaryButtonSx}>Choose video</Button>
            </label>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>MP4, MOV, or WebM; up to 500 MB and 20 minutes.</Typography>
          </Box>
        )}

        {busy && (
          <Box>
            <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
              <Typography variant="caption">{status}…</Typography>
              <Typography variant="caption">{status === 'Uploading' ? `${progress}%` : 'Preparing playback'}</Typography>
            </Stack>
            <LinearProgress variant={status === 'Uploading' ? 'determinate' : 'indeterminate'} value={progress} />
          </Box>
        )}
        {error && <Alert severity="error">{error}</Alert>}
      </Stack>
    </Paper>
  );
};

export default CaseVideoUploadField;
